/**
 * Browser softphone call state: one active call plus at most one other call
 * ("waiting") — either a second inbound call still ringing, or a call the user
 * put on hold to take that second one.
 *
 * Pure so it can be unit tested (vitest runs in node here, no DOM). The
 * provider (GlobalVoiceProvider) owns the SDK call objects and side effects
 * (server hold/resume, accept, reject); this only says where each call sits.
 *
 *   no calls ── incoming ──▶ active: ringing
 *   active ──── incoming ──▶ waiting: ringing          (call waiting banner)
 *   active + waiting ── incoming ──▶ rejected (third call; logged as missed)
 *   holdAndAnswer: active → waiting (on-hold), waiting → active (connected)
 *   swap (both answered): active ⇄ waiting, the one moved back goes on hold
 *   ended(active): the waiting call (ringing or on hold) becomes active
 */

export type VoiceProvider = 'twilio' | 'telnyx';

export type LegStatus = 'connecting' | 'ringing' | 'connected' | 'on-hold';

export interface CallLeg {
  /** Local key for the SDK call object; stable for the leg's life. */
  key: string;
  provider: VoiceProvider;
  /** Server call id (Twilio "CA…", Telnyx "v3:…", or a temp "BROWSER-…" id). */
  callSid?: string;
  direction: 'inbound' | 'outbound';
  /** The other party's number. */
  number: string;
  fromNumber?: string;
  toNumber?: string;
  callerId?: string;
  callerName?: string;
  status: LegStatus;
  /** ms epoch the leg was created. */
  startTime: number;
  /** ms epoch the leg was first answered. */
  connectedAt?: number;
  /**
   * The server's hold routes work for this call (Twilio, or a Telnyx call
   * ringing through a conference). Otherwise hold falls back to the SDK's
   * local media hold.
   */
  serverHold: boolean;
}

export interface CallSlots {
  active: CallLeg | null;
  waiting: CallLeg | null;
}

export const EMPTY_SLOTS: CallSlots = { active: null, waiting: null };

export type IncomingDecision = 'ring' | 'wait' | 'reject';

/** What to do with a new inbound call given the calls already up. */
export function decideIncoming(s: CallSlots): IncomingDecision {
  if (!s.active) return 'ring';
  if (!s.waiting) return 'wait';
  return 'reject';
}

export type CallSlotsAction =
  | { type: 'incoming'; leg: CallLeg }
  | { type: 'outgoing'; leg: CallLeg }
  | { type: 'update'; key: string; patch: Partial<Omit<CallLeg, 'key'>> }
  | { type: 'answered'; key: string; at: number }
  | { type: 'ended'; key: string }
  | { type: 'holdAndAnswer'; at: number }
  | { type: 'swap' }
  | { type: 'setHeld'; key: string; held: boolean };

function patchLeg(leg: CallLeg | null, key: string, patch: Partial<CallLeg>): CallLeg | null {
  return leg && leg.key === key ? { ...leg, ...patch, key: leg.key } : leg;
}

export function callSlotsReducer(s: CallSlots, a: CallSlotsAction): CallSlots {
  switch (a.type) {
    case 'incoming': {
      const leg: CallLeg = { ...a.leg, status: 'ringing' };
      const d = decideIncoming(s);
      if (d === 'ring') return { active: leg, waiting: null };
      if (d === 'wait') return { ...s, waiting: leg };
      return s;
    }
    case 'outgoing':
      // The UI only dials with no call up; never replace a live call.
      return s.active ? s : { active: a.leg, waiting: null };
    case 'update':
      return { active: patchLeg(s.active, a.key, a.patch), waiting: patchLeg(s.waiting, a.key, a.patch) };
    case 'answered': {
      const answer = (leg: CallLeg | null) =>
        leg && leg.key === a.key && leg.status !== 'on-hold'
          ? { ...leg, status: 'connected' as const, connectedAt: leg.connectedAt ?? a.at }
          : leg;
      return { active: answer(s.active), waiting: answer(s.waiting) };
    }
    case 'ended':
      if (s.active?.key === a.key) return { active: s.waiting, waiting: null };
      if (s.waiting?.key === a.key) return { ...s, waiting: null };
      return s;
    case 'holdAndAnswer':
      if (!s.active || !s.waiting || s.waiting.status !== 'ringing') return s;
      return {
        active: { ...s.waiting, status: 'connected', connectedAt: s.waiting.connectedAt ?? a.at },
        waiting: { ...s.active, status: 'on-hold' },
      };
    case 'swap':
      if (!s.active || !s.waiting || s.waiting.status === 'ringing') return s;
      return {
        active: { ...s.waiting, status: 'connected' },
        waiting: { ...s.active, status: 'on-hold' },
      };
    case 'setHeld': {
      const set = (leg: CallLeg | null) =>
        leg && leg.key === a.key && (leg.status === 'connected' || leg.status === 'on-hold')
          ? { ...leg, status: a.held ? ('on-hold' as const) : ('connected' as const) }
          : leg;
      return { active: set(s.active), waiting: set(s.waiting) };
    }
    default:
      return s;
  }
}

/** The waiting banner offers Swap only once both calls have been answered. */
export function canSwap(s: CallSlots): boolean {
  return !!s.active && !!s.waiting && s.waiting.status === 'on-hold' &&
    (s.active.status === 'connected' || s.active.status === 'on-hold');
}

/**
 * The banner offers "Hold & answer" / "Decline" while a second call rings over
 * an answered one. (Two calls ringing at once: the first rings in the normal
 * incoming UI and the second just waits.)
 */
export function hasRingingWaiting(s: CallSlots): boolean {
  return !!s.active && (s.active.status === 'connected' || s.active.status === 'on-hold') &&
    s.waiting?.status === 'ringing';
}

// ── missed-call logging ────────────────────────────────────────────────────

const TWILIO_CALL_SID = /^CA[0-9a-f]{32}$/i;
const TELNYX_CALL_SID = /^v3:[A-Za-z0-9_-]{8,200}$/;

/** A call id /api/calls/record-missed accepts (mirrors the server check). */
export function isLoggableCallSid(callSid: unknown): callSid is string {
  return typeof callSid === 'string' && (TWILIO_CALL_SID.test(callSid) || TELNYX_CALL_SID.test(callSid));
}

export type EndReason = 'hangup' | 'remote' | 'canceled' | 'rejected' | 'error';

/**
 * The missed status to log for an inbound leg that ended, or null when it
 * should not be logged as missed (it was answered, or it was outbound).
 * `declined` = the user pressed Decline (or a third call was refused as busy).
 */
export function missedStatusFor(leg: Pick<CallLeg, 'direction' | 'connectedAt'>, reason: EndReason, declined: boolean, busy = false): string | null {
  if (leg.direction !== 'inbound' || leg.connectedAt) return null;
  if (busy) return 'busy';
  if (declined || reason === 'rejected') return 'no-answer';
  return 'canceled';
}

/**
 * Keys of the ringing inbound legs a server `call:ended` event is about (the
 * caller hung up before anyone answered). The event names the caller's call
 * and the agent legs dialed for it; a browser leg's own SID is one of those.
 * Answered legs are never matched: they end through their own media path.
 */
export function ringingLegsForEndedCall(slots: CallSlots, msg: { callSid?: unknown; legSids?: unknown } | null | undefined): string[] {
  if (!msg) return [];
  const sids = new Set<string>(
    [msg.callSid, ...(Array.isArray(msg.legSids) ? msg.legSids : [])].filter((s): s is string => typeof s === 'string' && s.length > 0),
  );
  if (sids.size === 0) return [];
  return [slots.active, slots.waiting]
    .filter((l): l is CallLeg => !!l && l.direction === 'inbound' && l.status === 'ringing' && !!l.callSid && sids.has(l.callSid))
    .map((l) => l.key);
}

/** Is any inbound call still ringing? (Ringing notifications can be closed when not.) */
export function hasRingingInbound(slots: CallSlots): boolean {
  return [slots.active, slots.waiting].some((l) => !!l && l.direction === 'inbound' && l.status === 'ringing');
}
